import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { LabelBox } from "../../../components";
import {
  useGetSurgeonsQuery,
  useRemoveDoctorFromHealthOrgMutation,
} from "../../../services/api";
import { CustomTable, StatusBadge } from "../../../components/DataTable";
import style from "../../Admin/admin.module.css";
import { CustomActionMenu } from "../../../components/Menu";
import { RootState, useAppSelector } from "../../../store";
import { ConfirmationDialog, DetailsDialog } from "../../../components/Dialog";
import { getErrorMessage } from "../../../utils/common";
import {
  buildUserActions,
  renderCellText,
  useStatusDialogs,
} from "../userShared";

/** Surgeon row with a derived speciality name for display. */
type SurgeonWithSpecialityName = Surgeon & { speciality_name: string };
/** Action identifiers for surgeon rows. */
type UserListAction = "view" | "logs" | "deassociate";

/** Props for the surgeon list tab. */
type SurgeonTabProps = {
  searchKey?: string;
  onClearSearch?: () => void;
};

/**
 * Formats a surgeon name for list rendering.
 *
 * Args:
 *   item: Surgeon row data from the API.
 *
 * Returns:
 *   JSX.Element: Rendered name text.
 */
const renderNameCell = (item: NewSurgeon) =>
  renderCellText(
    `${item?.surname?.toUpperCase() ?? ""}, ${item?.given_name ?? ""} ${
      item?.prefix ?? ""
    }`,
    { className: style.cell }
  );

/**
 * Renders a status badge for list rows.
 *
 * Args:
 *   isActive: Status flag from the API.
 *
 * Returns:
 *   JSX.Element: Status badge component.
 */
const renderStatusBadge = (isActive?: boolean) => (
  <StatusBadge isActive={isActive ?? false} />
);

/**
 * Renders the surgeon list tab with search and status actions.
 *
 * Args:
 *   searchKey: Optional search query.
 *
 * Returns:
 *   JSX.Element: Surgeon list tab content.
 */
const SurgeonTab = ({ searchKey, onClearSearch }: SurgeonTabProps) => {
  const { id } = useParams();
  const healthOrgId = Number.isFinite(Number(id)) ? Number(id) : undefined;
  const navigate = useNavigate();
  const [removeDoctorFromHealthOrg] = useRemoveDoctorFromHealthOrgMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedUser, setSelectedUser] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    confirmOpen,
    logsOpen,
    openConfirm,
    closeConfirm,
    openLogs,
    closeLogs,
  } = useStatusDialogs();

  const { specialities } = useAppSelector(
    (state: RootState) => state.referenceData
  );

  const specialityMap = useMemo(
    () => new Map(specialities.map((s) => [s.id, s.name])),
    [specialities]
  );

  const [queryParam, setQueryParam] = useState({
    search_key: searchKey,
    health_org_ids: healthOrgId,
    is_include_all_doctor: false,
    is_detailed: true,
    order_by_columns: "surname",
    order: "Asc",
    doctor_id: undefined,
  });

  useEffect(() => {
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: searchKey === "" ? undefined : searchKey,
    }));
  }, [searchKey]);

  const fields = [
    {
      id: "surname",
      label: "Name",
      flex: 2,
      sortable: true,
      type: "name",
      render: (item: NewSurgeon) => renderNameCell(item),
    },
    {
      id: "speciality_name",
      label: "Speciality",
      flex: 2,
      sortable: true,
      render: (item: SurgeonWithSpecialityName) => {
        const specialityName =
          item.speciality_name ||
          specialityMap.get(item.speciality_id ?? 0) ||
          "";
        return renderCellText(specialityName, {
          className: style.cell,
          title: specialityName,
        });
      },
    },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      sortable: true,
      render: (item: NewSurgeon) => renderStatusBadge(item.is_active),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      flex: 0.5,
      render: (item: Users) => {
        const actions = buildUserActions<UserListAction>([
          "view",
          "logs",
          "deassociate",
        ]);

        return (
          <CustomActionMenu<Users, UserListAction>
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleMenu}
            actions={actions}
          />
        );
      },
    },
  ];

  const { data: surgeonData, isFetching, refetch } = useGetSurgeonsQuery(queryParam);

  const surgeonTableData = useMemo<SurgeonWithSpecialityName[]>(() => {
    const items = surgeonData?.items ?? [];
    return items.map((surgeon: Surgeon) => ({
      ...surgeon,
      speciality_name: surgeon.speciality_id
        ? specialityMap.get(surgeon.speciality_id) ?? ""
        : "",
    }));
  }, [specialityMap, surgeonData]);

  const handleMenu = (value: UserListAction, admin: Users) => {
    setSelectedUser(admin);
    switch (value) {
      case "deassociate":
        openConfirm();
        break;
      case "view":
        navigate(`/users/details/${admin?.id}`, {
          state: { userType: "DOCTOR", source: "USER_DETAILS", healthOrgId },
        });
        break;
      case "logs":
        openLogs();
        break;
      default:
        break;
    }
  };

  const onSubmit = async () => {
    if (!selectedUser?.id || !healthOrgId) {
      setMessage({
        type: "error",
        text: "No doctor selected for deassociation.",
      });
      return;
    }
    setIsLoading(true);
    try {
      await removeDoctorFromHealthOrg({
        doctor_id: selectedUser.id,
        health_org_id: healthOrgId,
      }).unwrap();
      setMessage({
        type: "success",
        text: "Doctor deassociated successfully.",
      });
      refetch();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        <CustomTable<SurgeonWithSpecialityName>
          fields={fields}
          paginationRequired={true}
          data={surgeonTableData}
          loading={isFetching}
          onDoubleClick={(admin: NewSurgeon) => {
            navigate(`/users/details/${admin?.id}`, {
              state: { userType: "DOCTOR", healthOrgId },
            });
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchKey}
          onClearSearch={onClearSearch}
        />
      </Box>
      {confirmOpen && (
        // Deactivation requires a reason; activation proceeds immediately.
        <ConfirmationDialog
          visible={confirmOpen}
          message={(() => {
            const surName = selectedUser?.surname ?? "";
            const givenName = selectedUser?.given_name ?? "";
            const prefix = selectedUser?.prefix ?? "";
            const formatted = `${surName}, ${givenName} ${prefix}`.trim();
            return `Are you sure you want to deassociate "${formatted}"?`;
          })()}
          onClose={() => {
            closeConfirm();
          }}
          onConfirm={() => {
            closeConfirm();
            onSubmit();
          }}
        />
      )}
      {logsOpen && (
        <DetailsDialog
          visible={logsOpen}
          details={selectedUser}
          onClose={() => {
            closeLogs();
          }}
          onConfirm={() => {
            closeLogs();
          }}
        />
      )}
    </>
  );
};

export default SurgeonTab;
